export interface GeoPosition {
    lat: number;
    lng: number;
    accuracy: number;
}

export interface GeoError {
    code: 'UNSUPPORTED' | 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT';
    message: string;
}

export interface GeoResult {
    position: GeoPosition | null;
    error: GeoError | null;
}

const ERROR_MESSAGES: Record<number, GeoError> = {
    1: {
        code: 'PERMISSION_DENIED',
        message: 'Location permission was denied. Allow location access for this site in your browser settings, or set the pin manually on the map.',
    },
    2: {
        code: 'POSITION_UNAVAILABLE',
        message: 'Your device could not determine its location. Try again, or set the pin manually on the map.',
    },
    3: {
        code: 'TIMEOUT',
        message: 'Location request timed out. Try again, or set the pin manually on the map.',
    },
};

/**
 * Never rejects. Callers get both the position (if any) and a reason when
 * there isn't one — geolocation failures were previously swallowed into a
 * single generic "couldn't read location" message, which made a denied
 * permission and a browser that silently blocks an insecure (non-HTTPS)
 * origin look identical and impossible to diagnose from the UI.
 */
export function getCurrentPosition(timeoutMs = 10000): Promise<GeoResult> {
    return new Promise((resolve) => {
        if (!('geolocation' in navigator)) {
            resolve({ position: null, error: { code: 'UNSUPPORTED', message: 'This browser does not support location services.' } });
            return;
        }
        if (!window.isSecureContext) {
            resolve({
                position: null,
                error: {
                    code: 'UNSUPPORTED',
                    message: 'Location requires a secure (HTTPS) connection, or localhost. Set the pin manually on the map instead.',
                },
            });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({
                position: { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy },
                error: null,
            }),
            (err) => resolve({ position: null, error: ERROR_MESSAGES[err.code] ?? { code: 'POSITION_UNAVAILABLE', message: err.message } }),
            { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
        );
    });
}
